import { useEffect, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'

import { Box, Button, Typography } from '@mui/material'
import { useTheme } from '@mui/material/styles'
import request from 'api'
import { Loading } from 'components/ui'
import { useDateTime, useMappedTranslation } from 'hooks'
import { getGlobalConsentApi } from 'pages/policies/api'
import { Consent, appendLegacyParams, buildPolicySearch } from 'pages/policies/utils'
import Detail from 'pages/policies/views/detail'
import i18n from 'utils/i18n'

export default function GlobalPrivacyNotices() {
  const t = useMappedTranslation()
  const theme = useTheme()
  const dateTime = useDateTime()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const paramCountryCode = searchParams.get('countryCode') || undefined
  const paramLocale = searchParams.get('locale') || undefined
  const policySearch = buildPolicySearch(searchParams)

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [consent, setConsent] = useState<Consent>()
  const [detail, setDetail] = useState<string>('<div/>')

  const getDetailApi = (uri: string) => {
    const LEGACY_HOST = 'https://account.samsung.com'
    return request
      .get(uri)
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentBody')
        }

        const document = new DOMParser().parseFromString(res.data, 'text/html')
        const language = paramLocale?.replace('-', '_') || i18n.language?.replace('-', '_')

        if (language) {
          document.querySelectorAll('a').forEach((tag) => {
            if (tag.href.includes(`${LEGACY_HOST}`)) {
              tag.href = appendLegacyParams(tag.href, language, paramCountryCode)
            }
          })
        }

        const body = document.getElementsByTagName('body').item(0)?.innerHTML
        if (body !== undefined) {
          const sanitized = body.replace(
            'body { word-wrap:break-word; word-break:keep-all; padding:1.5em; }',
            'body { word-wrap:break-word; word-break:keep-all; }'
          )
          setDetail(sanitized)
        }
      })
      .catch(() => setDetail('<div>Failed to load</div>'))
  }

  const handleNoticeDetailList = () => {
    navigate(`/policies/privacy-notices${policySearch}`)
  }

  const renderActions = () => {
    return (
      <>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            pt: { xs: '52px', md: '72px' }
          }}
        >
          <Typography
            className='off'
            fontSize='13px'
            lineHeight='16px'
            fontWeight='700'
            color={theme.palette.mode === 'dark' ? '#999999' : '#636363'}
          >
            {t('iam.01.Common.Txt.Effective.date', { 0: dateTime(Number(consent?.publish), { format: 'noticeDate' }) })}
          </Typography>
          <Box
            sx={{
              marginTop: '12px'
            }}
          >
            <Button
              sx={{
                padding: '7px 12px',
                fontSize: '13px',
                lineHeight: '19px',
                minHeight: '0',
                textTransform: 'none',
                color: 'text.primary',
                textDecoration: 'none',
                border: `0.5px solid #636363`,
                borderRadius: '24px',
                textAlign: 'center',
                minWidth: { xs: '152px', md: '198px' },
                display: 'block',
                height: '33px',
                fontWeight: 500
              }}
              data-testid='LinkD3D811E0'
              onClick={handleNoticeDetailList}
            >
              {t('iam.01.Common.Notice.detail.list.01')}
            </Button>
          </Box>
        </Box>
      </>
    )
  }

  useEffect(() => {
    setIsLoading(true)

    getGlobalConsentApi('PN', false, { overrides: { countryCode: paramCountryCode, locale: paramLocale } })
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentApi')
        } else {
          const data = res.data[0] as Consent
          setConsent(data)
          return data
        }
      })
      .then((data) => {
        return getDetailApi(data.uri)
      })
      .finally(() => {
        setIsLoading(false)
      })
  }, [paramCountryCode, paramLocale])

  return (
    <Box sx={{ width: '100%', height: '100%', padding: { xs: '24px', lg: '0' }, paddingTop: { xs: '5px', lg: '46px' } }}>
      {isLoading ? (
        <Loading fullScreen />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: { xs: '100%', lg: '1040px' } }}>
            <Detail title={t('iam.02.Agree.Terms.PP.title.01')} detail={detail} action={renderActions()} isLoading={false} />
          </Box>
        </Box>
      )}
    </Box>
  )
}
